import { Check, Circle } from 'lucide-react';
import type { ReactNode } from 'react';
import type { RequestCompletion as Completion, RequestCompletionPart } from '@nest-recruit/shared';
import styles from './RequestCompletion.module.css';

/**
 * ADR 0075 (section B Q3 a): how complete the request is. Two parts must be filled to send (with the position and the start
 * date); the rest count towards the bar. Each line jumps to its part of the form, so the lazy path is "click what's open".
 */

export function CompletionBar({ percent, label }: { readonly percent: number; readonly label: string }): ReactNode {
  return (
    <div className={styles.barWrap}>
      <div className={styles.barHead}>
        <span className={styles.barLabel}>{label}</span>
        <span className={styles.barValue}>{percent}%</span>
      </div>
      <div className={styles.bar} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <span className={styles.fill} data-full={percent >= 100} style={{ inlineSize: `${percent}%` }} />
      </div>
    </div>
  );
}

export function RequestCompletionList({ completion, onJump }: { readonly completion: Completion; readonly onJump: (part: RequestCompletionPart) => void }): ReactNode {
  return (
    <div className={styles.card}>
      <CompletionBar percent={completion.percent} label="Filled in so far" />
      <ul className={styles.list}>
        {completion.items.map((item) => {
          const isDone = item.share >= 1;
          return (
            <li key={item.part}>
              <button type="button" className={styles.item} data-done={isDone} onClick={() => onJump(item.part)}>
                <span className={styles.icon} aria-hidden>
                  {isDone ? <Check size={14} /> : <Circle size={14} />}
                </span>
                <span className={styles.itemLabel}>
                  {item.label}
                  {item.isRequired && !isDone && <span className={styles.required}> · needed to send</span>}
                </span>
                {item.share > 0 && item.share < 1 && <span className={styles.part}>{Math.floor(item.share * 100)}%</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
